import React from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import PieChats from "../../graphs/PieChats";
import { GRAPH_OPTIONS, TABLE_OPTIONS, TABLE_COLUMNS, TABLE_STYLES, PERSONA_BAR_PALETTE } from "../constants";
import { GroupedBarTooltip } from "./shared";
import uiConfig from "../../../config/uiConfig";

const CustomLayouts = ({
  customLayouts,
  loading,
  configLoaded,
  charts,
  tables,
  reportsByPersonaData,
  personaNames,
  pieKeys,
  abbreviateReportName,
}) => {
  // Show skeleton layout during initial loading before config is loaded
  if (loading && !configLoaded) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="grid gap-4 p-4" style={{ gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}>
          {[1, 2].map((i) => (
            <div key={i} className="flex flex-col h-[400px] rounded-lg border border-slate-200 bg-white overflow-hidden">
              <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100 bg-slate-50">
                <div>
                  <div className="h-2 w-10 bg-gray-100 rounded animate-pulse mb-1" />
                  <div className="h-4 w-24 bg-gray-100 rounded animate-pulse" />
                </div>
              </div>
              <div className="flex-1 p-3 overflow-hidden">
                <LoadingSkeleton type={i === 1 ? "graph" : "table"} />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (customLayouts.length === 0) return null;

  return (
    <>
      {customLayouts.map((layout) => (
        <div key={layout.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="grid gap-4 p-4" style={{ gridTemplateColumns: `repeat(${layout.columns}, minmax(0, 1fr))` }}>
            {layout.slots?.map((slot, slotIndex) => {
              if (!slot) {
                return (
                  <div
                    key={slotIndex}
                    className="h-[400px] rounded-lg bg-slate-50 border-2 border-dashed border-slate-200"
                  />
                );
              }
              // Use custom title if provided, otherwise fallback to default label
              const defaultLabel =
                slot.type === "graph"
                  ? GRAPH_OPTIONS.find((g) => g.id === slot.id)?.label
                  : TABLE_OPTIONS.find((t) => t.id === slot.id)?.label;
              const slotLabel = slot.title || defaultLabel;

              return (
                <div
                  key={slotIndex}
                  className="flex flex-col h-[400px] rounded-lg border border-slate-200 bg-white overflow-hidden"
                >
                  <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100 bg-slate-50">
                    <div>
                      <p className="text-[9px] font-bold uppercase tracking-wide text-slate-400">{slot.type}</p>
                      <p className="text-sm font-semibold text-slate-700">{slotLabel}</p>
                    </div>
                  </div>
                  <div className="flex-1 p-3 overflow-hidden">
                    {loading ? (
                      <LoadingSkeleton type={slot.type} />
                    ) : (
                      <SlotContent
                        slot={slot}
                        charts={charts}
                        tables={tables}
                        reportsByPersonaData={reportsByPersonaData}
                        personaNames={personaNames}
                        pieKeys={pieKeys}
                        abbreviateReportName={abbreviateReportName}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </>
  );
};

const LoadingSkeleton = ({ type }) => (
  <div className="h-full flex flex-col">
    {type === "graph" ? (
      <div className="h-full flex items-end justify-between gap-2 px-2">
        {[60, 85, 45, 90, 55, 75, 40].map((h, i) => (
          <div key={i} className="flex-1 rounded-t bg-gray-100 animate-pulse" style={{ height: `${h}%` }} />
        ))}
      </div>
    ) : (
      <div className="space-y-2">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="h-6 bg-gray-100 rounded animate-pulse" />
        ))}
      </div>
    )}
  </div>
);

const SlotContent = ({ slot, charts, tables, reportsByPersonaData, personaNames, pieKeys, abbreviateReportName }) => {
  // Graph content
  if (slot.type === "graph") {
    switch (slot.id) {
      case "viewsByPersona":
        return reportsByPersonaData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={reportsByPersonaData} margin={{ top: 4, right: 10, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="reportName" tick={{ fontSize: 10, fill: "#9ca3af" }} tickLine={false} interval={0} tickFormatter={abbreviateReportName} />
              <YAxis tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
              <Tooltip content={<GroupedBarTooltip />} cursor={{ fill: "#f3f4f6" }} />
              <Legend wrapperStyle={{ fontSize: "10px" }} />
              {personaNames.map((persona, i) => (
                <Bar key={persona} dataKey={persona} name={persona} fill={PERSONA_BAR_PALETTE[i % PERSONA_BAR_PALETTE.length]} radius={[2, 2, 0, 0]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        ) : <EmptySlot />;

      case "dailyActivity":
        return charts.byDate?.dates?.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={charts.byDate.dates.map((d, i) => ({ date: d, views: charts.byDate.views[i] ?? 0 }))} margin={{ top: 4, right: 10, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#9ca3af" }} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ fontSize: "11px", borderRadius: "6px" }} />
              <Bar dataKey="views" fill={uiConfig.chartPrimaryColor || "#3b82f6"} radius={[2, 2, 0, 0]} name="Views" />
            </BarChart>
          </ResponsiveContainer>
        ) : <EmptySlot />;

      case "reportDistribution":
        return pieKeys.length > 0 ? (
          <PieChats data={charts.byGroup} selectedCategory={pieKeys[0]} showLegend={true} chartType="donut" />
        ) : <EmptySlot />;

      case "topReportsGraph":
        return tables.topReports.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={tables.topReports.slice(0, 8).map((row) => ({ name: row.reportName, views: row.count }))} layout="vertical" margin={{ top: 4, right: 10, left: 5, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis type="number" tick={{ fontSize: 10, fill: "#9ca3af" }} />
              <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 9, fill: "#6b7280" }} />
              <Tooltip />
              <Bar dataKey="views" fill="var(--app-button-color)" radius={[0, 2, 2, 0]} name="Views" />
            </BarChart>
          </ResponsiveContainer>
        ) : <EmptySlot />;

      case "userActivityGraph":
        return tables.userActivity.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={tables.userActivity.slice(0, 8).map((row) => ({ name: row.email, views: row.views }))} layout="vertical" margin={{ top: 4, right: 10, left: 5, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis type="number" tick={{ fontSize: 10, fill: "#9ca3af" }} />
              <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 8, fill: "#6b7280" }} />
              <Tooltip />
              <Bar dataKey="views" fill="var(--app-icon-color)" radius={[0, 2, 2, 0]} name="Views" />
            </BarChart>
          </ResponsiveContainer>
        ) : <EmptySlot />;

      default:
        return <EmptySlot />;
    }
  }

  // Table content
  if (slot.type === "table") {
    const columns = TABLE_COLUMNS[slot.id];
    if (!columns) return <EmptySlot />;
    
    const data = tables[slot.id] || [];
    return (
      <TableWrapper isEmpty={data.length === 0}>
        <ConfigTable columns={columns} data={data.slice(0, 10)} tableId={slot.id} />
      </TableWrapper>
    );
  }

  return <EmptySlot />;
};

// Generic table component using column config from constants
const ConfigTable = ({ columns, data, tableId }) => {
  const { row, badge } = TABLE_STYLES;
  const badgeColors = badge[tableId] || badge.default;

  // Header styles from uiConfig
  const headerStyle = {
    background: uiConfig.tableAccentBackground || "#e0f2fe",
    color: uiConfig.tableAccentColor || "#bb1ccc",
    fontWeight: 600,
  };

  return (
    <table className="modern-table w-full text-xs">
      <thead className="sticky top-0" style={{ background: headerStyle.background }}>
        <tr style={{ borderBottom: `1px solid ${headerStyle.background}` }}>
          {columns.map((col) => (
            <th
              key={col.key}
              className={`px-2 py-2 ${col.align === "right" ? "text-right" : "text-left"}`}
              style={headerStyle}
            >
              {col.header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {data.map((rowData, i) => (
          <tr key={i} className={`border-b ${row.border} ${row.hover}`}>
            {columns.map((col) => {
              const value = col.key === "index" ? i + 1 : (rowData[col.key] ?? col.fallback ?? "");
              const cellClass = `px-2 py-2 ${col.align === "right" ? `text-right font-medium ${row.text}` : col.key === "index" ? row.indexText : row.text}`;
              const truncateStyle = col.truncate && col.maxWidth ? { maxWidth: col.maxWidth } : {};

              if (col.badge) {
                return (
                  <td key={col.key} className="px-2 py-2">
                    <span className={`${badgeColors} text-[10px] font-semibold px-2 py-0.5 rounded-full`}>
                      {value}
                    </span>
                  </td>
                );
              }

              return (
                <td
                  key={col.key}
                  className={`${cellClass} ${col.truncate ? "truncate" : ""}`}
                  style={truncateStyle}
                  title={col.truncate ? value : undefined}
                >
                  {value}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const EmptySlot = () => (
  <div className="h-full flex items-center justify-center text-xs text-slate-400">No data available</div>
);

const TableWrapper = ({ children, isEmpty }) => (
  <div className="h-full overflow-auto">
    {children}
    {isEmpty && <EmptySlot />}
  </div>
);

export default CustomLayouts;
